<template>
  <v-dialog
    v-model="dialog"
    max-width="700"
  >
    <v-card v-if="vereador">
      <v-card-title>Substituir Veículo</v-card-title>

      <v-card-text>
        <p>
          Vereador: <strong>{{ vereador.nome }}</strong>
        </p>

        <v-alert
          v-if="!podeSubstituir && !loadingPermissao && mostradoPermissao"
          type="warning"
          outlined
          class="mb-4"
        >
          Este vereador não pode substituir o veículo no momento.
        </v-alert>

        <ModalBuscaPlacaDetran
          v-if="podeSubstituir"
          :key="buscaKey"
          @sucesso="onPlacaConfirmada"
          @update="onPlacaConfirmada"
        />

        <template v-if="podeSubstituir">
          <v-divider class="my-6" />

          <div class="text-subtitle-1 font-weight-bold mb-2">
            Documentos
          </div>

          <v-alert
            variant="outlined"
            type="info"
            class="mb-4"
          >
            <div><strong>Obrigatórios:</strong></div>
            <ol class="mb-0">
              <li>CRLV</li>
              <li>Contrato de Locação/Cessão (somente se o veículo não estiver em nome do vereador)</li>
            </ol>
          </v-alert>

          <v-alert
            v-if="mensagemDocsObrigatorios"
            variant="outlined"
            type="warning"
            class="mb-4"
          >
            {{ mensagemDocsObrigatorios }}
          </v-alert>

          <v-row>
            <v-col
              cols="6"
              md="6"
              style="margin-top: 8px"
            >
              <UploadFile
                key="crlv"
                nome="CRLV *"
                :file-type-erro="filesTypeErros.includes('crlv')"
                :file-name="docs.crlv?.name"
                :is-foto="false"
                :disabled="loading || loadingPermissao"
                :type-file-required="'PDF'"
                @file="(file) => onChangeDocFile(file, 'crlv')"
              />
              <div
                v-if="filesErros.includes('crlv')"
                class="text-caption text-error mt-1"
              >
                Arquivo obrigatório.
              </div>
            </v-col>

            <v-col
              cols="6"
              style="margin-top: 8px"
            >
              <UploadFile
                key="contrato"
                :nome="requiresContrato
                  ? 'Contrato de Locação/Cessão (Obrigatório) *'
                  : 'Contrato de Locação/Cessão (Opcional)'"
                :file-type-erro="filesTypeErros.includes('contrato')"
                :file-name="docs.contrato?.name"
                :is-foto="false"
                :disabled="loading || loadingPermissao"
                :type-file-required="'PDF'"
                @file="(file) => onChangeDocFile(file, 'contrato')"
              />
              <div
                v-if="requiresContrato && filesErros.includes('contrato')"
                class="text-caption text-error mt-1"
              >
                Arquivo obrigatório.
              </div>
            </v-col>
          </v-row>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          text
          :disabled="loading || loadingPermissao"
          @click="fechar"
        >
          Cancelar
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!canConfirm"
          @click="confirmar"
        >
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ModalBuscaPlacaDetran from './ModalBuscaPlacaDetran.vue';
import cpvService from '@/services/cpv/cpv.service';
import GlobalStore from '@/store/modules/global';
import UploadFile from '@/components/UploadFile.vue';

export default {
  name: 'ModalSubstituicaoVeiculo',
  components: { ModalBuscaPlacaDetran, UploadFile },

  props: {
    value: Boolean,
    vereador: Object,
  },

  data() {
    return {
      veiculo: null,

      podeSubstituir: false,
      loadingPermissao: false,
      mostradoPermissao: false,

      loading: false,

      docs: {
        crlv: null,
        contrato: null,
      },

      filesTypeErros: [],
      filesErros: [],

      buscaKey: 0,
    };
  },

  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },

    placaNormalizada() {
      const p = this.veiculo?.placa || '';
      return (p || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 7);
    },

    cpfVereadorDigits() {
      return String(this.vereador?.cpf || '').replace(/\D/g, '');
    },

    cpfProprietarioDigits() {
      return String(this.veiculo?.cpfCnpjProprietario || '').replace(/\D/g, '');
    },

    requiresContrato() {
      if (!this.veiculo) return false;

      const cpfV = this.cpfVereadorDigits;
      const cpfP = this.cpfProprietarioDigits;

      if (!cpfV || !cpfP) return false;

      return cpfV !== cpfP;
    },

    docsObrigatoriosPendentes() {
      const pendentes = [];

      if (!this.docs.crlv) pendentes.push('CRLV');
      if (this.requiresContrato && !this.docs.contrato) {
        pendentes.push('Contrato de Locação/Cessão');
      }

      return pendentes;
    },

    mensagemDocsObrigatorios() {
      if (!this.veiculo) return '';

      if (this.requiresContrato && !this.docs.contrato) {
        return 'Como o veículo não está em nome do vereador, o Contrato de Locação/Cessão é obrigatório.';
      }

      if (!this.docsObrigatoriosPendentes.length) return '';

      if (this.docsObrigatoriosPendentes.length === 1) {
        return `Documento obrigatório pendente: ${this.docsObrigatoriosPendentes[0]}.`;
      }

      return `Documentos obrigatórios pendentes: ${this.docsObrigatoriosPendentes.join(', ')}.`;
    },

    docsOk() {
      const crlvOk = !!this.docs.crlv;
      const contratoOk = this.requiresContrato ? !!this.docs.contrato : true;

      return crlvOk && contratoOk;
    },

    canConfirm() {
      return (
        !!this.veiculo &&
        this.podeSubstituir &&
        !this.loadingPermissao &&
        !this.loading &&
        this.placaNormalizada.length === 7 &&
        this.docsOk &&
        this.filesTypeErros.length === 0
      );
    },
  },

  watch: {
    dialog(val) {
      if (val && this.vereador?.id) this.validarPermissao();
      if (!val) this.resetState();
    },

    vereador: {
      deep: true,
      handler(v) {
        if (this.dialog && v?.id) this.validarPermissao();
      },
    },
  },

  methods: {
    showSnack(mensagem, type = 'warning') {
      GlobalStore.setSnackBar({
        show: true,
        message: mensagem,
        type,
        timeout: 4000,
      });
    },

    toBool(v) {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'true' || s === '1') return true;
        if (s === 'false' || s === '0') return false;
      }
      return false;
    },

    onChangeDocFile(file, campo) {
      this.filesErros = this.filesErros.filter((f) => f !== campo);

      if (!file) {
        this.docs[campo] = null;
        this.filesTypeErros = this.filesTypeErros.filter((f) => f !== campo);
        return;
      }

      const fileName = String(file.name || '').toLowerCase();
      const mimeType = String(file.type || '').toLowerCase();

      const ok = mimeType
        ? mimeType === 'application/pdf'
        : fileName.endsWith('.pdf');

      if (ok) {
        this.docs[campo] = file;
        this.filesTypeErros = this.filesTypeErros.filter((f) => f !== campo);
      } else {
        this.docs[campo] = null;
        if (!this.filesTypeErros.includes(campo)) {
          this.filesTypeErros = [...this.filesTypeErros, campo];
        }
      }
    },

    validateDocumentos() {
      const required = ['crlv'];
      if (this.requiresContrato) required.push('contrato');

      const missing = required.filter((k) => !this.docs[k]);
      this.filesErros = missing;

      return missing.length === 0;
    },

    validarPermissao() {
      this.loadingPermissao = true;
      this.mostradoPermissao = false;
      this.podeSubstituir = false;

      cpvService.vereadorCanUpdateVehicle(this.vereador.id).subscribe({
        next: (res) => {
          const body = res?.data ?? res;
          const raw = body?.result ?? body;
          this.podeSubstituir = this.toBool(raw);

          this.loadingPermissao = false;
          this.mostradoPermissao = true;
        },
        error: (err) => {
          this.loadingPermissao = false;
          this.mostradoPermissao = true;

          const message =
            err?.response?.data?.message ||
            'Erro ao validar permissão de substituição.';

          this.showSnack(message, 'warning');
        },
      });
    },

    onPlacaConfirmada(payload) {
      const placa = payload?.placa || payload?.plate || '';

      this.veiculo = {
        placa,
        marcaModelo: payload?.marcaModelo || '',
        cor: payload?.cor || '',
        anoFabricacao: payload?.anoFabricacao || '',
        anoModelo: payload?.anoModelo || '',
        chassi: payload?.chassi || '',
        renavam: payload?.renavam || '',
        cpfCnpjProprietario:
          payload?.cpfCnpjProprietario ||
          payload?.cpfCnpj ||
          payload?.cpfProprietario ||
          '',
      };

      this.filesErros = [];
    },

    confirmar() {
      if (!this.canConfirm) return;

      if (this.filesTypeErros.length) {
        this.showSnack('Corrija os documentos com formato inválido. Apenas PDF é permitido.', 'warning');
        return;
      }

      if (!this.validateDocumentos()) {
        this.showSnack('Anexe os documentos obrigatórios para finalizar.', 'warning');
        return;
      }

      this.loading = true;

      const payload = {
        vereadorId: this.vereador.id,
        placaVeiculo: this.placaNormalizada,
        marcaModelo: this.veiculo.marcaModelo,
        cor: this.veiculo.cor,
        anoFabricacao: this.veiculo.anoFabricacao,
        anoModelo: this.veiculo.anoModelo,
        chassi: this.veiculo.chassi,
        renavam: this.veiculo.renavam,
        cpfCnpjProprietario: this.veiculo.cpfCnpjProprietario,
      };

      const formData = new FormData();

      formData.append(
        'payload',
        new File([JSON.stringify(payload)], 'payload.json', { type: 'application/json' })
      );

      formData.append(
        'crlvDocumento',
        this.docs.crlv,
        this.docs.crlv.name
      );

      if (this.docs.contrato) {
        formData.append(
          'locacaoDocumento',
          this.docs.contrato,
          this.docs.contrato.name
        );
      }

      cpvService.updateVehiclePlate(formData).subscribe(
        (res) => {
          this.loading = false;

          const message =
            res?.data?.message ||
            res?.message ||
            'Veículo substituído com sucesso.';

          this.showSnack(message, 'success');
          this.dialog = false;
          this.$emit('substituido');
          this.resetState();
        },
        (err) => {
          this.loading = false;

          const message =
            err?.response?.data?.message ||
            'Erro ao substituir veículo.';

          this.showSnack(message, 'warning');
        }
      );
    },

    fechar() {
      this.dialog = false;
      this.resetState();
    },

    resetState() {
      this.veiculo = null;
      this.podeSubstituir = false;
      this.loadingPermissao = false;
      this.mostradoPermissao = false;
      this.loading = false;

      this.docs = {
        crlv: null,
        contrato: null,
      };

      this.filesTypeErros = [];
      this.filesErros = [];

      this.buscaKey++;
    },
  },
};
</script>